import { createRoute, useNavigate } from '@tanstack/react-router';
import { LoadingState } from '@verydia/ui';
import { useEffect, useRef } from 'react';
import { rootRoute } from '../root.tsx';
import { completeAuthorization, loadAuthConfig, takePending } from '../../lib/auth/index.ts';
import { useAuth } from '../../lib/auth/index.ts';

/**
 * Where Cognito sends the browser back with an authorization code.
 *
 * Everything here is one-shot: the code, the PKCE verifier and the state token
 * are each consumed exactly once, and the URL is replaced immediately afterwards
 * so the code does not sit in history where a later visitor could replay it.
 */
function CallbackPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  // React 19 in development mounts effects twice. The code is single-use, so a
  // second exchange would fail and turn a successful sign-in into an error page.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const pending = takePending();

    const fail = (code: string) =>
      void navigate({ to: '/auth/error', search: { reason: code }, replace: true });

    void (async () => {
      const error = params.get('error');
      if (error !== null) {
        // `login_required` means a silent re-authentication found no Cognito
        // session — expected after the session expires, and not an error worth
        // showing. Send the user to sign in instead.
        if (error === 'login_required' || error === 'interaction_required') {
          void navigate({ to: '/login', replace: true });
          return;
        }
        fail(error);
        return;
      }

      const code = params.get('code');
      const state = params.get('state');

      if (pending === null || code === null || state === null) {
        fail('invalid_callback');
        return;
      }

      // The CSRF check. A callback whose state does not match one this tab
      // issued was not started here, and its code must not be exchanged.
      if (state !== pending.state) {
        fail('state_mismatch');
        return;
      }

      try {
        const tokens = await completeAuthorization(
          loadAuthConfig(import.meta.env),
          code,
          pending.verifier,
        );
        auth.completeSignIn(tokens);
        // `replace`, so the back button cannot return to a URL holding a code.
        void navigate({ to: pending.returnTo || '/', replace: true });
      } catch (thrown) {
        fail(
          thrown instanceof Error && 'code' in thrown
            ? String(thrown.code)
            : 'token_exchange_failed',
        );
      }
    })();
  }, [auth, navigate]);

  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--vc-surface-sunken)]">
      <LoadingState label="Completing sign-in" />
    </main>
  );
}

export const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: CallbackPage,
  validateSearch: (search: Record<string, unknown>) => search,
});
